<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import UploadField from "@/components/UploadField.vue";
import RichTextField from "@/components/RichTextField.vue";
import { uploadImage } from "@/lib/uploads";
import { toDateTimeLocal } from "@/lib/date";
import { fetchAnnouncement, fetchTopics, addAnnouncement, editAnnouncement, sendTopicNotification } from "./api";

const route = useRoute(), router = useRouter();
const id = computed(() => route.params.id || null);
const isEdit = computed(() => !!id.value);
const BTN_LINKS = ["Kronos", "IHP", "Success Factor", "Concur", "Zendesk", "Campaign", "Videos", "More Options"];
const BTN_LINK_OPTIONS = [{ title: "Custom URL…", value: "" }, ...BTN_LINKS.map((o) => ({ title: o, value: o }))];

const loading = ref(false), notFound = ref(false), saving = ref(false), success = ref(false), error = ref(""), validationError = ref("");
const allTopics = ref([]);
let original = null;
const form = ref({ topic: [], notif_title: "", notif_content: "", title: "", content: "", startDate: "", endDate: "", btnTitle: "", btnLink: "", redirectUrl: "", imgUrl: "", isExternalBrowser: false });
const sendNotif = ref(true);

const imageUploader = (file, p) => uploadImage("announcements", file, p);

onMounted(async () => {
  loading.value = true;
  try {
    allTopics.value = await fetchTopics().catch(() => []);
    if (isEdit.value) {
      const a = await fetchAnnouncement(id.value);
      if (!a) { notFound.value = true; return; }
      original = a;
      form.value = { topic: Array.isArray(a.topic) ? a.topic : a.topic ? [a.topic] : [], notif_title: a.notif_title ?? "", notif_content: a.notif_content ?? "", title: a.title ?? "", content: a.content ?? "", startDate: toDateTimeLocal?.(a.startDate) ?? "", endDate: toDateTimeLocal?.(a.endDate) ?? "", btnTitle: a.btnTitle ?? "", btnLink: a.btnLink ?? "", redirectUrl: a.redirectUrl ?? "", imgUrl: a.imgUrl ?? "", isExternalBrowser: !!a.isExternalBrowser };
    }
  } catch { error.value = "Couldn't load announcement."; }
  finally { loading.value = false; }
});

function validate() {
  const f = form.value;
  if (!f.topic.length) return "Select at least one topic.";
  if (!f.notif_title.trim()) return "Enter a notification title.";
  if (!f.notif_content.trim()) return "Enter notification content.";
  if (!f.title.trim()) return "Enter a title.";
  if (!f.content.trim() || f.content === "<p></p>") return "Enter announcement content.";
  if (!f.startDate) return "Choose a start date.";
  if (!f.endDate) return "Choose an end date.";
  if (new Date(f.endDate) < new Date(f.startDate)) return "End date must be after start date.";
  return "";
}

async function save() {
  success.value = false; error.value = ""; validationError.value = "";
  const msg = validate();
  if (msg) { validationError.value = msg; return; }
  const f = form.value;
  const payload = { ...(original || {}), imgUrl: f.imgUrl, notif_title: f.notif_title, notif_content: f.notif_content, title: f.title, content: f.content, startDate: new Date(f.startDate), endDate: new Date(f.endDate), btnTitle: f.btnTitle, btnLink: f.btnLink, redirectUrl: f.redirectUrl, topic: f.topic, isExternalBrowser: f.isExternalBrowser };
  saving.value = true;
  try {
    const result = isEdit.value ? await editAnnouncement(id.value, payload) : await addAnnouncement(payload);
    if (sendNotif.value) {
      await sendTopicNotification({ notif_title: f.notif_title, notif_content: f.notif_content, title: f.title, bodyMessage: f.content, topic: f.topic, startDate: f.startDate, endDate: f.endDate, objectId: result?._id ?? result }).catch(() => {});
    }
    success.value = true;
    setTimeout(() => router.push({ name: "announcements" }), 800);
  } catch { error.value = "Couldn't save. Please try again."; }
  finally { saving.value = false; }
}
</script>

<template>
  <section>
    <VBtn variant="text" prepend-icon="mdi:arrow-left" class="mb-4" @click="router.push({ name: 'announcements' })">Back</VBtn>
    <h1 class="text-h5 mb-4">{{ isEdit ? "Edit Announcement" : "Add Announcement" }}</h1>
    <p v-if="loading">Loading…</p>
    <VAlert v-else-if="notFound" type="error">Announcement not found.</VAlert>
    <VCard v-else class="pa-4" max-width="900">
      <VAlert v-if="error" type="error" class="mb-4">{{ error }}</VAlert>
      <VAlert v-if="success" type="success" class="mb-4">Saved. Redirecting…</VAlert>
      <form @submit.prevent="save">
        <VRow>
          <VCol cols="12">
            <div class="text-body-2 font-weight-semibold mb-1">Topics</div>
            <VAutocomplete v-model="form.topic" :items="allTopics.map(t => ({ title: t.topic, value: t.topic }))" item-title="title" item-value="value" label="Topics" multiple chips closable-chips auto-select-first />
          </VCol>
          <VCol cols="12" md="6"><VTextField v-model="form.notif_title" label="Notification Title" /></VCol>
          <VCol cols="12" md="6"><VTextField v-model="form.notif_content" label="Notification Content" /></VCol>
          <VCol cols="12"><VDivider class="my-2" /></VCol>
          <VCol cols="12"><VTextField v-model="form.title" label="Title" /></VCol>
          <VCol cols="12"><RichTextField v-model="form.content" label="Content" :image-uploader="imageUploader" /></VCol>
          <VCol cols="12" md="6"><VTextField v-model="form.startDate" label="Start Date" type="datetime-local" /></VCol>
          <VCol cols="12" md="6"><VTextField v-model="form.endDate" label="End Date" type="datetime-local" /></VCol>
          <VCol cols="12"><UploadField v-model="form.imgUrl" kind="image" label="Cover Image" :uploader="imageUploader" /></VCol>
          <VCol cols="12" md="6"><VTextField v-model="form.btnTitle" label="Button Title" /></VCol>
          <VCol cols="12" md="6"><VSelect v-model="form.btnLink" :items="BTN_LINK_OPTIONS" item-title="title" item-value="value" label="Button Link" /></VCol>
          <VCol v-if="form.btnLink === ''" cols="12"><VTextField v-model="form.redirectUrl" label="Button URL" /></VCol>
          <VCol cols="12">
            <VCheckbox v-model="form.isExternalBrowser" label="Open link in external browser" />
            <VCheckbox v-model="sendNotif" label="Send push notification to selected topics" />
          </VCol>
        </VRow>
        <VAlert v-if="validationError" type="warning" class="mt-4 mb-2">{{ validationError }}</VAlert>
        <div class="d-flex gap-2 mt-4">
          <VBtn type="submit" :loading="saving">{{ isEdit ? "Update" : "Add Announcement" }}</VBtn>
          <VBtn variant="tonal" @click="router.push({ name: 'announcements' })">Cancel</VBtn>
        </div>
      </form>
    </VCard>
  </section>
</template>
